import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { UserWithoutPassword } from '@geely/shared';
import { authApi } from '@/lib/api';

interface AuthContextType {
  user: UserWithoutPassword | null;
  login: (email: string, password: string) => Promise<UserWithoutPassword>;
  logout: () => void;
  isAuthenticated: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserWithoutPassword | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('geely_token');
    if (!token) {
      setLoading(false);
      return;
    }

    authApi
      .me()
      .then((response) => {
        setUser(response.data);
        localStorage.setItem('geely_user', JSON.stringify(response.data));
      })
      .catch(() => {
        localStorage.removeItem('geely_token');
        localStorage.removeItem('geely_user');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<UserWithoutPassword> => {
    const response = await authApi.login(email, password);
    const { token, user: userData } = response.data;

    // Store credentials
    localStorage.setItem('geely_token', token);
    localStorage.setItem('geely_user', JSON.stringify(userData));

    // Update state synchronously
    setUser(userData);

    // Return user data so caller can use it immediately
    return userData;
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem('geely_token');
    localStorage.removeItem('geely_user');
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        isAuthenticated: !!user,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
